import { useContext, useState } from 'react';
import * as R from 'ramda';

import AttributeDetails from '@atom/components/common/attributeDetails/AttributeDetails';
import ElementMediaPane from '@atom/components/common/elementMedia/ElementMediaPane';
import { Icon, Progress, Tabs } from '@atom/mui';
import { FolderIcon, type IconComponent, InfoIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { SubjectType } from '@atom/types/media';
import { AttributesType } from '@atom/types/schema';

import SubItemsContext, { SubItemAttributeUpdate } from './SubItemsContext';

import classes from './subItemTree.module.css';

type SubItemView = 'info' | 'media';

const VIEWS: { value: SubItemView; icon: IconComponent }[] = [
  { value: 'info', icon: InfoIcon },
  { value: 'media', icon: FolderIcon },
];

const tabsSx = {
  minHeight: 'unset',
  padding: '0.5rem 0 0 0.25rem',
  borderBottom: `1px solid ${colors.neutral.typhoon}`,
  '& .MuiTabs-indicator': { height: '2px' },
};

const tabSx = {
  width: '5.125rem',
  minWidth: '5.125rem',
  minHeight: 'unset',
  padding: '0.5rem 0',
  marginRight: '1.5rem',
  color: colors.neutral.gray,
  '&.Mui-selected': { color: colors.brand.blue },
};

type AttributeUpdatePayload = {
  id: string;
  attributeGroupName: string;
  attributes: SubItemAttributeUpdate;
};

const SubItemDetailPane = () => {
  const {
    rootAsset,
    tree,
    selectedId,
    permissions,
    rootSelectable = false,
    media,
    getAttributeDetailsProps,
    updateElementAttributes,
    applyAttributeChange,
  } = useContext(SubItemsContext);

  const [activeView, setActiveView] = useState<SubItemView>('info');
  const [loadingAttributeGroups, setLoadingAttributeGroups] = useState<
    string[]
  >([]);

  if (!selectedId || (selectedId === rootAsset.id && !rootSelectable)) {
    return <div />;
  }

  const detail = tree.details[selectedId];

  if (!detail) {
    return tree.status[selectedId] === 'loading' ? (
      <div className={classes.detailPaneSpinner}>
        <Progress />
      </div>
    ) : (
      <div />
    );
  }

  const onPendingApproval = (
    action: string,
    attributeGroupName: string,
    attribute: AttributesType,
  ) =>
    applyAttributeChange(action === 'approve' ? 'approve' : 'reject', {
      assetId: selectedId,
      attributeId: attribute.id,
      changeId: attribute.changeId,
      rootAssetId: rootAsset.id,
    });

  const onAttributeUpdate = async (payload: AttributeUpdatePayload) => {
    setLoadingAttributeGroups(current => [
      ...current,
      payload.attributeGroupName,
    ]);

    try {
      await updateElementAttributes(selectedId, payload.attributes);
    } finally {
      setLoadingAttributeGroups(R.without([payload.attributeGroupName]));
    }
  };

  return (
    <div>
      <div className={classes.detailPaneHeader}>
        <div className={classes.detailPaneName} data-cy="subItemDetailName">
          {detail.name}
        </div>
      </div>
      <Tabs
        value={activeView}
        onChange={(_, value) => setActiveView(value as SubItemView)}
        sx={tabsSx}
        data-cy="subItemDetailTabs"
      >
        {VIEWS.map(view => (
          <Tabs.Tab
            key={view.value}
            value={view.value}
            label={<Icon color="inherit" icon={view.icon} />}
            sx={tabSx}
            data-cy={`subItemDetailTab-${view.value}`}
          />
        ))}
      </Tabs>
      {activeView === 'info' ? (
        <AttributeDetails
          asset={detail}
          loading={loadingAttributeGroups}
          onAttributeUpdate={onAttributeUpdate}
          onPendingApproval={onPendingApproval}
          canManageChanges={permissions.canManageChanges}
          canUpdate={permissions.canUpdateAttributes}
          inputWidth="25vw"
          {...getAttributeDetailsProps?.(detail)}
        />
      ) : (
        <ElementMediaPane
          subjectId={detail.id}
          parentSubjectId={media?.parentSubjectId ?? rootAsset.id}
          subjectType={media?.subjectType ?? SubjectType.INVENTORY_ASSET}
          canCreate={permissions.canCreateMedia}
          canUpdate={permissions.canUpdateMedia}
          canDelete={permissions.canDeleteMedia}
          coverPhotoMediaId={media?.coverPhotoMediaId}
          onCoverPhotoToggle={media?.onCoverPhotoToggle}
        />
      )}
    </div>
  );
};

export default SubItemDetailPane;
